import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Cookie, Settings } from "lucide-react";
import { useLocalizedPath } from "./Header";

export default function CookieBanner({ onAccept, onReject }) {
  const [showSettings, setShowSettings] = useState(false);
  const { t } = useTranslation();
  const { getPath } = useLocalizedPath();
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const handleSaveSettings = () => {
    onAccept();
  };

  const handleAcceptAll = () => {
    setPreferences({
      necessary: true,
      analytics: true,
      marketing: true,
    });
    onAccept();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-end justify-center p-4 cookie-banner-enter"
      data-testid="cookie-banner"
    >
      <Card className="w-full max-w-2xl bg-white shadow-2xl border-0">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Cookie className="h-5 w-5 text-orange-500" />
              </div>
              <CardTitle className="text-xl font-['Manrope']">{t('cookieBanner.title')}</CardTitle>
            </div>
          </div>
          <CardDescription className="pt-2 text-gray-600">
            {t('cookieBanner.description')}
          </CardDescription>
        </CardHeader>

        {showSettings && (
          <CardContent className="pt-4 border-t">
            <div className="space-y-4">
              {/* Necessary cookies */}
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <Label className="text-base font-medium">{t('cookieBanner.necessary.title')}</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('cookieBanner.necessary.description')}
                  </p>
                </div>
                <Switch checked disabled className="data-[state=checked]:bg-gray-400" />
              </div>

              {/* Analytics cookies */}
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <Label className="text-base font-medium">{t('cookieBanner.analytics.title')}</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('cookieBanner.analytics.description')}
                  </p>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, analytics: checked })
                  }
                  data-testid="cookie-analytics-switch"
                />
              </div>

              {/* Marketing cookies */}
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <Label className="text-base font-medium">{t('cookieBanner.marketing.title')}</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('cookieBanner.marketing.description')}
                  </p>
                </div>
                <Switch
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, marketing: checked })
                  }
                  data-testid="cookie-marketing-switch"
                />
              </div>
            </div>
          </CardContent>
        )}

        <CardFooter className="flex flex-col sm:flex-row gap-3 pt-4">
          {!showSettings ? (
            <>
              <Button
                variant="outline"
                className="w-full sm:w-auto border-gray-300"
                onClick={onReject}
                data-testid="cookie-reject-button"
              >
                {t('cookieBanner.rejectAll')}
              </Button>
              <Button
                variant="outline"
                className="w-full sm:w-auto border-gray-300"
                onClick={() => setShowSettings(true)}
                data-testid="cookie-settings-button"
              >
                <Settings className="h-4 w-4 mr-2" />
                {t('cookieBanner.settings')}
              </Button>
              <Button
                className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
                onClick={handleAcceptAll}
                data-testid="cookie-accept-button"
              >
                {t('cookieBanner.acceptAll')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                className="w-full sm:w-auto border-gray-300"
                onClick={() => setShowSettings(false)}
                data-testid="cookie-back-button"
              >
                {t('cookieBanner.back')}
              </Button>
              <Button
                className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
                onClick={handleSaveSettings}
                data-testid="cookie-save-button"
              >
                {t('cookieBanner.saveSettings')}
              </Button>
            </>
          )}
        </CardFooter>

        <div className="px-6 pb-4 text-center">
          <Link
            to={getPath('privacy')}
            className="text-sm text-gray-500 hover:text-orange-500 transition-colors"
            data-testid="cookie-privacy-link"
          >
            {t('cookieBanner.moreInfo')}
          </Link>
        </div>
      </Card>
    </div>
  );
}
